import {
  ACTIVITY_MODE_WALK,
  isActivityModeCompatible,
  normalizeActivityMode,
} from "./activityMode.js";
import { queueSaveNavGridPersistent } from "./navGridPersistent.js";
import { filterPointsByNodes } from "./navGridPointFilter.js";
import { readNavGridMemory, writeNavGridMemory } from "./navGridMemory.js";
export { filterPointsByNodes } from "./navGridPointFilter.js";

const NAV_GRID_KEY = "trekkin_nav_grid_v1";
let __lastGraphCache = null;
const NAVGRID_TERRAIN_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN;
const __navgridTerrainTileCache = new Map();

function readMemNavGrid() {
  return readNavGridMemory();
}

function writeMemNavGrid(grid) {
  writeNavGridMemory(grid);
}

function hasNavGridTerrainToken() {
  return typeof NAVGRID_TERRAIN_TOKEN === "string" && NAVGRID_TERRAIN_TOKEN.trim().length > 0;
}

function getTileCoords(lat, lon, z) {
  const tileX = Math.floor(((lon + 180) / 360) * 2 ** z);
  const tileY = Math.floor(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      2 ** z
  );
  return { tileX, tileY };
}

async function loadTerrainTile(z, tileX, tileY) {
  const key = `${z}/${tileX}/${tileY}`;
  if (__navgridTerrainTileCache.has(key)) {
    return __navgridTerrainTileCache.get(key);
  }

  const url = `https://api.mapbox.com/v4/mapbox.terrain-rgb/${z}/${tileX}/${tileY}.pngraw?access_token=${NAVGRID_TERRAIN_TOKEN}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("terrain_tile_fetch_failed");

  const blob = await res.blob();
  let bitmap = null;
  if (typeof createImageBitmap === "function") {
    bitmap = await createImageBitmap(blob);
  } else if (typeof Image !== "undefined") {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    bitmap = await new Promise((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
  } else {
    throw new Error("terrain_bitmap_unavailable");
  }

  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(bitmap.width, bitmap.height)
      : Object.assign(document.createElement("canvas"), {
          width: bitmap.width,
          height: bitmap.height,
        });
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("terrain_canvas_ctx_missing");
  ctx.drawImage(bitmap, 0, 0);

  const entry = { canvas, ctx, size: bitmap.width };
  __navgridTerrainTileCache.set(key, entry);
  return entry;
}

async function sampleTerrainElevation(lat, lon, z = 13) {
  if (!hasNavGridTerrainToken()) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const { tileX, tileY } = getTileCoords(lat, lon, z);
  const tile = await loadTerrainTile(z, tileX, tileY);
  const scale = 2 ** z * 256;
  const px = Math.round(((lon + 180) / 360) * scale - tileX * 256);
  const py = Math.round(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      scale -
      tileY * 256
  );
  const x = Math.max(0, Math.min(tile.size - 1, px));
  const y = Math.max(0, Math.min(tile.size - 1, py));
  const { data } = tile.ctx.getImageData(x, y, 1, 1);
  if (!data || data.length < 3) return null;
  if (data[0] === 0 && data[1] === 0 && data[2] === 0) return null;
  const elevation = -10000 + (data[0] * 256 ** 2 + data[1] * 256 + data[2]) * 0.1;
  return Number.isFinite(elevation) ? Math.round(elevation) : null;
}

export function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function interpolate(a, b, t) {
  const hasEleA = Number.isFinite(Number(a?.ele ?? a?.alt ?? a?.elevation));
  const hasEleB = Number.isFinite(Number(b?.ele ?? b?.alt ?? b?.elevation));
  const eleA = hasEleA ? Number(a.ele ?? a.alt ?? a.elevation) : NaN;
  const eleB = hasEleB ? Number(b.ele ?? b.alt ?? b.elevation) : NaN;
  return {
    lat: a.lat + (b.lat - a.lat) * t,
    lon: a.lon + (b.lon - a.lon) * t,
    ele: hasEleA && hasEleB ? eleA + (eleB - eleA) * t : undefined,
  };
}

export function loadNavGrid() {
  try {
    const mem = readMemNavGrid();

    // A grelha nativa pode ultrapassar meio milhão de pontos. Depois de
    // hidratada, a cópia em memória é a fonte mais recente e evita voltar a
    // ler, fazer JSON.parse e normalizar dezenas de MB em cada cálculo.
    if (mem?.segments?.length) return mem;

    const raw = localStorage.getItem(NAV_GRID_KEY);

    const defaultGrid = {
      version: 1,
      spacingMeters: 50,
      mergeMeters: 15,
      routeMergeMeters: 30,
      entryPromptMeters: 200,
      segments: [],
      updatedAt: 0,
    };

    if (!raw) {
      return defaultGrid;
    }

    const parsed = JSON.parse(raw);
    const baseMerge = Number(parsed?.mergeMeters) || 15;
    const rawRouteMerge = Number(parsed?.routeMergeMeters);
    const routeMerge =
      Number.isFinite(rawRouteMerge) && rawRouteMerge > 0
        ? Math.max(baseMerge, rawRouteMerge)
        : Math.max(baseMerge, 30);

    const local = {
      version: 1,
      spacingMeters: Number(parsed?.spacingMeters) || 50,
      mergeMeters: baseMerge,
      routeMergeMeters: routeMerge,
      entryPromptMeters: Number(parsed?.entryPromptMeters) || 200,
      segments: normalizeNavGrid({ segments: parsed?.segments })?.segments || [],
      updatedAt: Number(parsed?.updatedAt) || 0,
    };

    writeMemNavGrid(local);
    return local;
  } catch {
    const mem = readMemNavGrid();
    if (mem?.segments?.length) return mem;
    return {
      version: 1,
      spacingMeters: 50,
      mergeMeters: 15,
      routeMergeMeters: 30,
      entryPromptMeters: 200,
      segments: [],
      updatedAt: 0,
    };
  }
}

export function saveNavGrid(grid, options = {}) {
  writeMemNavGrid(grid);
  const forceSkipPersistentQueue = options?.skipPersistentQueue === true;
  const hasIOSNativeBridge = (() => {
    try {
      return (
        typeof window !== "undefined" &&
        typeof window?.webkit?.messageHandlers?.nativeBridge?.postMessage === "function"
      );
    } catch {
      return false;
    }
  })();
  const nativePlatform = (() => {
    try {
      if (typeof window === "undefined") return "";
      if (hasIOSNativeBridge) return "ios";
      const native = !!window?.Capacitor?.isNativePlatform?.();
      const platform = String(window?.Capacitor?.getPlatform?.() || "").toLowerCase();
      return native ? platform : "";
    } catch {
      return "";
    }
  })();
  const isNativeIOS = nativePlatform === "ios";
  const isNative = !!nativePlatform;
  const needsLocalStorageJson = !isNative;
  // A app iOS usa uma bridge WKWebView própria, sem Filesystem do Capacitor.
  // Não serializar a grelha completa aqui: com mais de 20 MB esta operação
  // fazia o processo WebContent reiniciar após editar/dividir um trecho.
  const needsIosSizeCheck = isNativeIOS && !hasIOSNativeBridge && !forceSkipPersistentQueue;
  const json = needsLocalStorageJson || needsIosSizeCheck ? (() => {
    try {
      return JSON.stringify(grid);
    } catch {
      return null;
    }
  })() : null;
  // Em native, a grelha vive em memória + Filesystem. Evita ocupar a quota
  // limitada do localStorage, necessária para histórico e estado crítico.
  const skipLocalStorageWrite =
    isNative || (isNativeIOS && typeof json === "string" && json.length > 3_000_000);
  const skipPersistentQueue = (() => {
    try {
      if (typeof window === "undefined") return false;
      // A bridge iOS atual grava a NavGrid por chunks no sandbox nativo.
      // A exceção antiga impedia precisamente essa gravação no fecho/rearranque.
      if (hasIOSNativeBridge) return false;
      const safeMode =
        window.__TREKKIN_IOS_SAFE_MODE__ === true ||
        window.localStorage.getItem("trekkin.ios.safeMode") === "1";
      // iOS: evita I/O pesado assíncrono (IndexedDB/Filesystem) para grelhas grandes,
      // e sempre que safe mode estiver ativo.
      const hugeGrid = typeof json === "string" && json.length > 1_000_000;
      return isNativeIOS && !hasIOSNativeBridge && (safeMode || hugeGrid);
    } catch {
      return false;
    }
  })();
  let localOk = false;
  if (!skipLocalStorageWrite) {
    try {
      localStorage.setItem(NAV_GRID_KEY, typeof json === "string" ? json : JSON.stringify(grid));
      localOk = true;
    } catch {
      localOk = false;
    }
  } else {
    localOk = false;
  }
  const persistDelayMs = Math.max(0, Number(options?.persistDelayMs ?? (localOk ? 40 : 0)) || 0);
  if (typeof window !== "undefined" && !skipPersistentQueue && !forceSkipPersistentQueue) {
    Promise.resolve()
      .then(() =>
        queueSaveNavGridPersistent(grid, {
          // persistência robusta quase imediata para evitar perdas em refresh/fecho rápido
          delayMs: persistDelayMs,
        })
      )
      .catch(() => {});
  }
  return localOk;
}

export function withUpdatedAt(grid) {
  return {
    ...grid,
    updatedAt: Date.now(),
  };
}

export function normalizeNavGrid(raw) {
  const normalizeSegments = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      return Object.values(value).filter((seg) => seg && typeof seg === "object");
    }
    return [];
  };

  const baseMerge = Number(raw?.mergeMeters) || 15;
  const rawRouteMerge = Number(raw?.routeMergeMeters);
  const routeMerge = Number.isFinite(rawRouteMerge) && rawRouteMerge > 0
    ? Math.max(baseMerge, rawRouteMerge)
    : Math.max(baseMerge, 30);
  return {
    version: 1,
    spacingMeters: Number(raw?.spacingMeters) || 50,
    mergeMeters: baseMerge,
    routeMergeMeters: routeMerge,
    entryPromptMeters: Number(raw?.entryPromptMeters) || 200,
    segments: normalizeSegments(raw?.segments),
    updatedAt: Number(raw?.updatedAt) || 0,
  };
}

export function applyRemoteNavGrid(remote, options = {}) {
  if (!remote || typeof remote !== "object") return false;
  const incoming = normalizeNavGrid(remote);
  const local = loadNavGrid();
  const force = options?.force === true;
  const incomingStamp = incoming.updatedAt || 0;
  const localStamp = local.updatedAt || 0;
  const incomingHasSegments = Array.isArray(incoming.segments) && incoming.segments.length > 0;
  const localHasSegments = Array.isArray(local.segments) && local.segments.length > 0;

  // Caso especial (migração / dados antigos):
  // Se o Firebase não tiver `updatedAt` (ou for 0) e o dispositivo estiver vazio,
  // aplicamos a grelha para não ficar tudo "sem segments" (muito comum após reinstalar no iOS).
  if (!force && incomingStamp <= localStamp) {
    if (!localHasSegments && incomingHasSegments) {
      saveNavGrid({ ...incoming, updatedAt: Date.now() });
      return true;
    }
    return false;
  }

  // Tenta guardar em cache rápido (mem/localStorage) e em persistência robusta (queueSave no saveNavGrid).
  saveNavGrid(incoming);
  return true;
}

export function resamplePoints(points, spacingMeters) {
  if (!Array.isArray(points) || points.length < 2) return [];
  const spacing = Math.max(5, Number(spacingMeters) || 50);
  const out = [];
  let prev = {
    lat: points[0].lat,
    lon: points[0].lon,
    ele: Number.isFinite(Number(points[0]?.ele ?? points[0]?.alt ?? points[0]?.elevation))
      ? Number(points[0].ele ?? points[0].alt ?? points[0].elevation)
      : undefined,
  };
  out.push(prev);

  let remaining = spacing;
  for (let i = 1; i < points.length; i++) {
    const curr = {
      lat: points[i].lat,
      lon: points[i].lon,
      ele: Number.isFinite(Number(points[i]?.ele ?? points[i]?.alt ?? points[i]?.elevation))
        ? Number(points[i].ele ?? points[i].alt ?? points[i].elevation)
        : undefined,
    };
    let segDist = haversineMeters(prev, curr);

    while (segDist >= remaining) {
      const ratio = remaining / segDist;
      const next = interpolate(prev, curr, ratio);
      out.push(next);
      prev = next;
      segDist = haversineMeters(prev, curr);
      remaining = spacing;
    }

    remaining -= segDist;
    prev = curr;
  }

  const last = points[points.length - 1];
  const lastOut = out[out.length - 1];
  // O extremo tem significado topológico: dois segmentos ligam-se pelos
  // extremos mesmo quando o último intervalo é menor do que o espaçamento.
  if (haversineMeters(lastOut, last) > 0.5) {
    out.push({
      lat: last.lat,
      lon: last.lon,
      ele: Number.isFinite(Number(last?.ele ?? last?.alt ?? last?.elevation))
        ? Number(last.ele ?? last.alt ?? last.elevation)
        : undefined,
    });
  }

  return out;
}

export function calcDistanceMeters(points) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineMeters(points[i - 1], points[i]);
  }
  return total;
}

export function optimizeStraightSegmentPoints(points, toleranceMeters = 0.75) {
  const source = Array.isArray(points) ? points : [];
  if (source.length <= 2 || calcDistanceMeters(source) <= 10) return source;
  const averageLat = source.reduce((sum, point) => sum + Number(point?.lat || 0), 0) / source.length;
  const xScale = 111320 * Math.max(0.1, Math.cos((averageLat * Math.PI) / 180));
  const yScale = 110540;
  const projected = source.map((point) => ({
    x: Number(point?.lon ?? point?.lng) * xScale,
    y: Number(point?.lat) * yScale,
  }));
  const toleranceSq = Math.max(0.05, Number(toleranceMeters) || 0.75) ** 2;
  const keep = new Uint8Array(source.length);
  keep[0] = 1;
  keep[source.length - 1] = 1;
  const stack = [[0, source.length - 1]];

  while (stack.length) {
    const [startIndex, endIndex] = stack.pop();
    const start = projected[startIndex];
    const end = projected[endIndex];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSq = dx * dx + dy * dy;
    let furthestIndex = -1;
    let furthestDistanceSq = -1;
    for (let index = startIndex + 1; index < endIndex; index += 1) {
      const point = projected[index];
      let ratio = lengthSq > 0
        ? ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq
        : 0;
      ratio = Math.max(0, Math.min(1, ratio));
      const offsetX = point.x - (start.x + ratio * dx);
      const offsetY = point.y - (start.y + ratio * dy);
      const distanceSq = offsetX * offsetX + offsetY * offsetY;
      if (distanceSq > furthestDistanceSq) {
        furthestDistanceSq = distanceSq;
        furthestIndex = index;
      }
    }
    if (furthestIndex > startIndex && furthestDistanceSq > toleranceSq) {
      keep[furthestIndex] = 1;
      stack.push([startIndex, furthestIndex], [furthestIndex, endIndex]);
    }
  }
  return source.filter((_, index) => keep[index] === 1);
}

export function createGridSegment({
  name,
  points,
  spacingMeters,
  premium = false,
  activityMode = ACTIVITY_MODE_WALK,
  simplifyStraightLines = false,
}) {
  const resampled = resamplePoints(points, spacingMeters);
  const storedPoints = simplifyStraightLines
    ? optimizeStraightSegmentPoints(resampled, 1.5)
    : resampled;
  return {
    id: `seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: name || "Trecho sem nome",
    createdAt: Date.now(),
    points: storedPoints,
    straightGeometrySimplified: simplifyStraightLines === true,
    premium: premium === true,
    activityMode: normalizeActivityMode(activityMode, ACTIVITY_MODE_WALK),
  };
}

export function filterNavGridByAccess(grid, opts = {}) {
  if (!grid || !Array.isArray(grid.segments)) return grid;
  const allowPremium = opts?.allowPremium === true;
  const activeMode = opts?.activityMode || null;
  const segments = grid.segments.filter((seg) => {
    if (!allowPremium && seg?.premium) return false;
    if (activeMode && !isActivityModeCompatible(seg?.activityMode, activeMode)) return false;
    return true;
  });
  if (segments.length === grid.segments.length) return grid;
  return { ...grid, segments };
}

export function addSegmentToGrid(grid, segment) {
  const next = {
    ...grid,
    segments: [...(grid.segments || []), segment],
  };
  saveNavGrid(next);
  return next;
}

export function removeSegmentFromGrid(grid, segmentId) {
  const next = {
    ...grid,
    segments: (grid.segments || []).filter((seg) => seg.id !== segmentId),
  };
  saveNavGrid(next);
  return next;
}

function findNearestNode(nodes, point, maxMeters) {
  let best = null;
  let bestDist = Infinity;
  for (const n of nodes) {
    const d = haversineMeters(n, point);
    if (d < bestDist) {
      best = n;
      bestDist = d;
    }
  }
  if (best && bestDist <= maxMeters) {
    return { node: best, dist: bestDist };
  }
  return null;
}

export function removePointsNear(grid, point, maxMeters) {
  if (!grid || !Array.isArray(grid.segments)) return { grid, removed: 0 };
  const threshold = Math.max(1, Number(maxMeters) || 0);
  let removed = 0;
  const segments = [];

  grid.segments.forEach((seg) => {
    const pts = Array.isArray(seg.points) ? seg.points : [];
    let segmentRemoved = 0;
    const kept = pts.filter((p) => {
      const d = haversineMeters(point, p);
      if (d <= threshold) {
        removed += 1;
        segmentRemoved += 1;
        return false;
      }
      return true;
    });
    if (kept.length >= 2) {
      segments.push(segmentRemoved ? { ...seg, points: kept, updatedAt: Date.now() } : seg);
    }
  });

  return {
    grid: { ...grid, segments },
    removed,
  };
}

export function movePointsNear(grid, from, to, maxMeters) {
  if (!grid || !Array.isArray(grid.segments)) return { grid, moved: 0 };
  const threshold = Math.max(1, Number(maxMeters) || 0);
  let moved = 0;
  const segments = grid.segments.map((seg) => {
    const pts = Array.isArray(seg.points) ? seg.points : [];
    let segmentMoved = 0;
    const nextPts = pts.map((p) => {
      const d = haversineMeters(from, p);
      if (d <= threshold) {
        moved += 1;
        segmentMoved += 1;
        return { ...p, lat: to.lat, lon: to.lon };
      }
      return p;
    });
    return segmentMoved ? { ...seg, points: nextPts, updatedAt: Date.now() } : seg;
  });
  return {
    grid: { ...grid, segments },
    moved,
  };
}

export function insertPointNear(grid, point, maxMeters) {
  if (!grid || !Array.isArray(grid.segments)) return { grid, inserted: false };
  const threshold = Math.max(1, Number(maxMeters) || 0);
  let best = null;
  grid.segments.forEach((seg, sIdx) => {
    const pts = Array.isArray(seg.points) ? seg.points : [];
    pts.forEach((p, idx) => {
      const d = haversineMeters(point, p);
      if (d <= threshold && (!best || d < best.dist)) {
        best = { sIdx, idx, dist: d };
      }
    });
  });
  if (!best) return { grid, inserted: false };

  const segments = grid.segments.map((seg, sIdx) => {
    if (sIdx !== best.sIdx) return seg;
    const pts = Array.isArray(seg.points) ? seg.points : [];
    const nextPts = [...pts];
    nextPts.splice(best.idx + 1, 0, { lat: point.lat, lon: point.lon });
    return { ...seg, points: nextPts };
  });

  return { grid: { ...grid, segments }, inserted: true };
}

export function insertPointInSegment(grid, segmentId, point) {
  if (!grid || !Array.isArray(grid.segments)) return { grid, inserted: false };
  const segments = grid.segments.map((seg) => {
    if (seg.id !== segmentId) return seg;
    const pts = Array.isArray(seg.points) ? seg.points : [];
    if (!pts.length) return seg;

    let bestIdx = 0;
    let bestDist = Infinity;
    pts.forEach((p, idx) => {
      const d = haversineMeters(point, p);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = idx;
      }
    });

    const nextPts = [...pts];
    nextPts.splice(bestIdx + 1, 0, { lat: point.lat, lon: point.lon });
    return { ...seg, points: nextPts };
  });

  return { grid: { ...grid, segments }, inserted: true };
}

export function insertPointInSegmentAtIndex(grid, segmentId, point, insertIdx) {
  if (!grid || !Array.isArray(grid.segments)) return { grid, inserted: false };
  const idx = Number.isFinite(insertIdx) ? Math.max(0, insertIdx) : null;
  const segments = grid.segments.map((seg) => {
    if (seg.id !== segmentId) return seg;
    const pts = Array.isArray(seg.points) ? seg.points : [];
    if (!pts.length) return seg;
    const safeIdx = idx == null ? pts.length - 1 : Math.min(idx, pts.length - 1);
    const nextPts = [...pts];
    nextPts.splice(safeIdx + 1, 0, { lat: point.lat, lon: point.lon });
    return { ...seg, points: nextPts };
  });
  return { grid: { ...grid, segments }, inserted: true };
}

export function fuseSegmentEndpoints(grid, segmentIds, maxMeters) {
  if (!grid || !Array.isArray(grid.segments)) {
    return { grid, updatedEndpoints: 0, clusterCount: 0 };
  }
  const ids = [...new Set(Array.isArray(segmentIds) ? segmentIds : [])].slice(0, 2);
  if (ids.length !== 2) return { grid, updatedEndpoints: 0, clusterCount: 0 };
  const allowed = new Set(ids);
  const endpoints = [];
  grid.segments.forEach((seg) => {
    if (!allowed.has(seg.id)) return;
    const pts = Array.isArray(seg.points) ? seg.points : [];
    if (!pts.length) return;
    endpoints.push({
      segId: seg.id,
      idx: 0,
      lat: pts[0].lat,
      lon: pts[0].lon,
    });
    if (pts.length > 1) {
      endpoints.push({
        segId: seg.id,
        idx: pts.length - 1,
        lat: pts[pts.length - 1].lat,
        lon: pts[pts.length - 1].lon,
      });
    }
  });

  if (endpoints.length < 2) {
    return { grid, updatedEndpoints: 0, clusterCount: 0 };
  }

  const threshold = Math.max(1, Number(maxMeters) || 0);
  if (!threshold) return { grid, updatedEndpoints: 0, clusterCount: 0 };

  // Uma fusão é sempre ponto-a-ponto: escolher apenas a extremidade mais
  // próxima de cada um dos dois trechos. Nunca criar grupos transitivos, pois
  // isso pode arrastar outras pontas próximas e deformar vários trechos.
  let closestPair = null;
  for (let i = 0; i < endpoints.length; i += 1) {
    for (let j = i + 1; j < endpoints.length; j += 1) {
      if (endpoints[i].segId === endpoints[j].segId) continue;
      const distance = haversineMeters(endpoints[i], endpoints[j]);
      if (distance > threshold) continue;
      if (!closestPair || distance < closestPair.distance) {
        closestPair = { a: endpoints[i], b: endpoints[j], distance };
      }
    }
  }
  if (!closestPair) {
    return { grid, updatedEndpoints: 0, clusterCount: 0 };
  }

  const updates = new Map();
  const lat = (closestPair.a.lat + closestPair.b.lat) / 2;
  const lon = (closestPair.a.lon + closestPair.b.lon) / 2;
  updates.set(`${closestPair.a.segId}:${closestPair.a.idx}`, { lat, lon });
  updates.set(`${closestPair.b.segId}:${closestPair.b.idx}`, { lat, lon });

  const segments = grid.segments.map((seg) => {
    if (!allowed.has(seg.id)) return seg;
    const pts = Array.isArray(seg.points) ? seg.points : [];
    if (!pts.length) return seg;
    const nextPts = pts.map((p, idx) => {
      const key = `${seg.id}:${idx}`;
      if (!updates.has(key)) return p;
      const next = updates.get(key);
      return { ...p, lat: next.lat, lon: next.lon };
    });
    return { ...seg, points: nextPts };
  });

  return {
    grid: { ...grid, segments },
    updatedEndpoints: 2,
    clusterCount: 1,
    fusedPair: closestPair,
  };
}

export function findNearestGridNode(grid, point, opts = {}) {
  if (!grid || !Array.isArray(grid.segments)) return null;
  const graph = buildNavGridGraph(grid, opts);
  let nearest = null;
  let bestDist = Infinity;
  graph.nodes.forEach((n) => {
    const d = haversineMeters(point, n);
    if (d < bestDist) {
      bestDist = d;
      nearest = n;
    }
  });
  if (!nearest) return null;
  return { node: nearest, dist: bestDist };
}

export function findNearestNodeInGraph(graph, point) {
  const nodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  if (!nodes.length) return null;
  let nearest = null;
  let bestDist = Infinity;
  nodes.forEach((n) => {
    if (!n || !Number.isFinite(n.lat) || !Number.isFinite(n.lon)) return;
    const d = haversineMeters(point, n);
    if (d < bestDist) {
      bestDist = d;
      nearest = n;
    }
  });
  if (!nearest) return null;
  return { node: nearest, dist: bestDist };
}

export function pickGridEntryNodeForWaypoint(graph, userPos, waypointPos, opts = {}) {
  const waypointNearest = findNearestNodeInGraph(graph, waypointPos);
  const waypointNode = waypointNearest?.node || null;
  if (!waypointNode?.id) return null;

  const edges = graph?.edges || {};
  const nodesById = graph?.nodesById || {};

  const component = new Set([waypointNode.id]);
  const queue = [waypointNode.id];
  while (queue.length) {
    const id = queue.shift();
    const neighbors = Object.keys(edges[id] || {});
    neighbors.forEach((nid) => {
      if (component.has(nid)) return;
      component.add(nid);
      queue.push(nid);
    });
  }

  const dist = {};
  const open = new Set(component);
  component.forEach((id) => {
    dist[id] = Infinity;
  });
  dist[waypointNode.id] = 0;

  while (open.size) {
    let current = null;
    let best = Infinity;
    open.forEach((id) => {
      const d = dist[id];
      if (d < best) {
        best = d;
        current = id;
      }
    });
    if (!current || !Number.isFinite(best)) break;
    open.delete(current);

    const neighbors = edges[current] || {};
    Object.entries(neighbors).forEach(([nid, weightRaw]) => {
      if (!open.has(nid)) return;
      const weight = Number(weightRaw);
      if (!Number.isFinite(weight) || weight < 0) return;
      const alt = best + weight;
      if (alt < dist[nid]) dist[nid] = alt;
    });
  }

  const preferEndpoints = opts?.preferEndpoints !== false;
  let candidateIds = Array.from(component);
  if (preferEndpoints) {
    const endpointIds = candidateIds.filter((id) => {
      const deg = Object.keys(edges[id] || {}).length;
      return deg <= 1;
    });
    if (endpointIds.length) candidateIds = endpointIds;
  }

  candidateIds = candidateIds.filter((id) => Number.isFinite(dist[id]));
  if (!candidateIds.length) return waypointNode;

  const minWalk = candidateIds.reduce((min, id) => Math.min(min, dist[id]), Infinity);
  const threshold = Number(opts?.thresholdMeters) || 200;
  const explicitExtra = Number(opts?.maxExtraWalkMeters);
  const maxExtraWalk = Number.isFinite(explicitExtra)
    ? Math.max(0, explicitExtra)
    : Math.max(250, threshold * 1.5);
  const shortlist = candidateIds.filter((id) => dist[id] <= minWalk + maxExtraWalk);
  const pool = shortlist.length ? shortlist : candidateIds;

  const driveWeightRaw = Number(opts?.driveWeight);
  const driveWeight = Number.isFinite(driveWeightRaw)
    ? Math.max(0, driveWeightRaw)
    : 0.35;

  const hasUser =
    Number.isFinite(Number(userPos?.lat)) &&
    Number.isFinite(Number(userPos?.lon ?? userPos?.lng));

  let bestNode = null;
  let bestScore = Infinity;
  let bestWalk = Infinity;
  let bestDrive = Infinity;

  pool.forEach((id) => {
    const node = nodesById[id];
    if (!node || !Number.isFinite(node.lat) || !Number.isFinite(node.lon)) return;
    const walk = dist[id];
    const drive = hasUser
      ? haversineMeters(
          { lat: Number(userPos.lat), lon: Number(userPos.lon ?? userPos.lng) },
          node
        )
      : 0;
    const score = walk + drive * driveWeight;
    const better =
      score < bestScore ||
      (score === bestScore && walk < bestWalk) ||
      (score === bestScore && walk === bestWalk && drive < bestDrive);
    if (better) {
      bestNode = node;
      bestScore = score;
      bestWalk = walk;
      bestDrive = drive;
    }
  });

  return bestNode || waypointNode;
}

export function buildNavGridGraph(grid, opts = {}) {
  const mergeMeters = Number(opts?.mergeMeters ?? grid?.mergeMeters) || 15;
  const mergeEndpointsOnly = opts?.mergeEndpointsOnly === true;
  const updatedAt = Number(grid?.updatedAt) || 0;
  const activeMode = opts?.activityMode || null;
  const segments = (Array.isArray(grid?.segments) ? grid.segments : []).filter((seg) =>
    activeMode ? isActivityModeCompatible(seg?.activityMode, activeMode) : true
  );
  const rawPointCount = segments.reduce(
    (total, seg) => total + (Array.isArray(seg?.points) ? seg.points.length : 0),
    0
  );
  const isNativeMobile = (() => {
    try {
      if (typeof window === "undefined") return false;
      if (window?.webkit?.messageHandlers?.nativeBridge) return true;
      return window?.Capacitor?.isNativePlatform?.() === true;
    } catch {
      return false;
    }
  })();
  const compactSpacingMeters =
    opts?.compactSpacingMeters === false
      ? 0
      : Number(opts?.compactSpacingMeters) > 0
      ? Math.max(8, Number(opts.compactSpacingMeters))
      : isNativeMobile && rawPointCount > 100_000
      ? Math.max(12, Math.min(20, mergeMeters))
      : 0;
  const segmentCount = segments.length;
  const firstSeg = segments[0];
  const lastSeg = segments[segments.length - 1];
  const firstPts = Array.isArray(firstSeg?.points) ? firstSeg.points.length : 0;
  const lastPts = Array.isArray(lastSeg?.points) ? lastSeg.points.length : 0;
  const cacheKey = `${updatedAt}:${segmentCount}:${mergeMeters}:${mergeEndpointsOnly ? "endpoints" : "all"}:${activeMode || "all"}:compact-${compactSpacingMeters || 0}:${firstSeg?.id || ""}:${lastSeg?.id || ""}:${firstPts}:${lastPts}`;

  if (__lastGraphCache && __lastGraphCache.key === cacheKey) {
    return __lastGraphCache.graph;
  }

  const nodes = [];
  const nodesById = {};
  const edges = {};
  const edgeAscent = {};
  const edgePaths = {};
  const segmentNodeIds = {};
  const spatialCellDeg = Math.max(mergeMeters, 8) / 111320;
  const spatialBuckets = new Map();

  const bucketCoords = (lat, lon) => ({
    x: Math.floor(Number(lon) / spatialCellDeg),
    y: Math.floor(Number(lat) / spatialCellDeg),
  });

  const bucketKey = (x, y) => `${x}:${y}`;

  const indexNodeToBucket = (node) => {
    if (!node || !Number.isFinite(node.lat) || !Number.isFinite(node.lon)) return;
    const c = bucketCoords(node.lat, node.lon);
    const key = bucketKey(c.x, c.y);
    const list = spatialBuckets.get(key) || [];
    list.push(node.id);
    spatialBuckets.set(key, list);
  };

  const findNearestIndexedNode = (point, maxMeters) => {
    if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) return null;
    const c = bucketCoords(point.lat, point.lon);
    let best = null;
    let bestDist = Infinity;
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const key = bucketKey(c.x + dx, c.y + dy);
        const ids = spatialBuckets.get(key);
        if (!ids || !ids.length) continue;
        for (const id of ids) {
          const n = nodesById[id];
          if (!n) continue;
          const d = haversineMeters(n, point);
          if (d < bestDist) {
            best = n;
            bestDist = d;
          }
        }
      }
    }
    if (best && bestDist <= maxMeters) return { node: best, dist: bestDist };
    return null;
  };

  const normalizePathPoints = (list) =>
    (Array.isArray(list) ? list : [])
      .map((p) => ({
        lat: Number(p?.lat),
        lon: Number(p?.lon ?? p?.lng),
        ele: Number.isFinite(Number(p?.ele ?? p?.alt ?? p?.elevation))
          ? Number(p.ele ?? p.alt ?? p.elevation)
          : undefined,
      }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));

  const computePositiveAscent = (list) => {
    if (!Array.isArray(list) || list.length < 2) return 0;
    let gain = 0;
    for (let i = 1; i < list.length; i += 1) {
      const a = Number(list[i - 1]?.ele);
      const b = Number(list[i]?.ele);
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
      const diff = b - a;
      if (diff > 0 && diff < 60) gain += diff;
    }
    return gain;
  };

  const estimateAscentBetweenNodes = (fromId, toId) => {
    const fromEle = Number(nodesById?.[fromId]?.ele);
    const toEle = Number(nodesById?.[toId]?.ele);
    if (!Number.isFinite(fromEle) || !Number.isFinite(toEle)) return 0;
    const diff = toEle - fromEle;
    if (diff > 0 && diff < 1200) return diff;
    return 0;
  };

  const ensureEdge = (a, b, dist, pathForward = null) => {
    if (!edges[a]) edges[a] = {};
    if (!edges[b]) edges[b] = {};
    if (!edgeAscent[a]) edgeAscent[a] = {};
    if (!edgeAscent[b]) edgeAscent[b] = {};
    if (!edgePaths[a]) edgePaths[a] = {};
    if (!edgePaths[b]) edgePaths[b] = {};
    if (!edges[a][b] || edges[a][b] > dist) {
      edges[a][b] = dist;
      edges[b][a] = dist;
      const normalized = normalizePathPoints(pathForward);
      if (normalized.length > 2) {
        // Guardar apenas uma orientação. A cópia invertida duplicava todos os
        // pontos da rede (mais de 500 mil atualmente) dentro do WebContent.
        // Ligações simples entre dois nós não precisam de geometria própria:
        // o cálculo reconstrói-as diretamente a partir dos nós. Evita manter
        // centenas de milhares de objetos duplicados no iOS.
        edgePaths[a][b] = normalized;
        delete edgePaths[b][a];
        const forwardAscent = computePositiveAscent(normalized);
        const reverseAscent = computePositiveAscent([...normalized].reverse());
        edgeAscent[a][b] =
          forwardAscent > 0 ? forwardAscent : estimateAscentBetweenNodes(a, b);
        edgeAscent[b][a] =
          reverseAscent > 0 ? reverseAscent : estimateAscentBetweenNodes(b, a);
      } else {
        delete edgePaths[a][b];
        delete edgePaths[b][a];
        edgeAscent[a][b] = estimateAscentBetweenNodes(a, b);
        edgeAscent[b][a] = estimateAscentBetweenNodes(b, a);
      }
    }
  };

  // Construir apenas com a coleção já filtrada pelo modo de atividade.
  // Usar novamente `grid.segments` anulava o filtro acima e fazia o iOS
  // materializar centenas de milhares de nós desnecessários.
  segments.forEach((seg) => {
    const refs = [];
    const sourcePoints = Array.isArray(seg.points) ? seg.points : [];
    const pts = seg?.straightGeometrySimplified === true
      ? resamplePoints(sourcePoints, 10)
      : compactSpacingMeters > 0
      ? resamplePoints(sourcePoints, compactSpacingMeters)
      : sourcePoints;
    for (let idx = 0; idx < pts.length; idx += 1) {
      const p = pts[idx];
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
      const canMergePoint =
        !mergeEndpointsOnly || idx === 0 || idx === pts.length - 1;
      const found = canMergePoint ? findNearestIndexedNode(p, mergeMeters) : null;
      let nodeId;
      if (found) {
        nodeId = found.node.id;
        const nextEle = Number(p?.ele ?? p?.alt ?? p?.elevation);
        if (Number.isFinite(nextEle)) {
          const prevSamples = Number(found.node._eleSamples || 0);
          const prevEle = Number(found.node.ele);
          if (Number.isFinite(prevEle) && prevSamples > 0) {
            found.node.ele = (prevEle * prevSamples + nextEle) / (prevSamples + 1);
            found.node._eleSamples = prevSamples + 1;
          } else {
            found.node.ele = nextEle;
            found.node._eleSamples = 1;
          }
        }
      } else {
        nodeId = `n-${nodes.length + 1}`;
        const nextEle = Number(p?.ele ?? p?.alt ?? p?.elevation);
        const node = {
          id: nodeId,
          lat: p.lat,
          lon: p.lon,
          ...(Number.isFinite(nextEle)
            ? { ele: nextEle, _eleSamples: 1 }
            : {}),
        };
        nodes.push(node);
        nodesById[nodeId] = node;
        indexNodeToBucket(node);
      }
      refs.push({
        id: nodeId,
        point: {
          lat: Number(p.lat),
          lon: Number(p.lon),
          ele: Number.isFinite(Number(p?.ele ?? p?.alt ?? p?.elevation))
            ? Number(p.ele ?? p.alt ?? p.elevation)
            : undefined,
        },
      });
    }

    const ids = refs.map((r) => r.id);
    segmentNodeIds[seg.id] = ids;
    if (refs.length < 2) return;

    let fromNodeId = refs[0].id;
    let prevPoint = refs[0].point;
    let edgePath = [prevPoint];
    let edgeDist = 0;

    for (let i = 1; i < refs.length; i += 1) {
      const currentRef = refs[i];
      const currentPoint = currentRef.point;
      const stepDist = haversineMeters(prevPoint, currentPoint);
      edgeDist += stepDist;
      edgePath.push(currentPoint);

      if (currentRef.id !== fromNodeId) {
        ensureEdge(fromNodeId, currentRef.id, edgeDist, edgePath);
        fromNodeId = currentRef.id;
        edgePath = [currentPoint];
        edgeDist = 0;
      }

      prevPoint = currentPoint;
    }
  });

  const graph = {
    nodes,
    nodesById,
    edges,
    edgeAscent,
    edgePaths,
    segmentNodeIds,
    spatialIndex: {
      cellDeg: spatialCellDeg,
      buckets: spatialBuckets,
    },
  };
  __lastGraphCache = { key: cacheKey, graph };
  return graph;
}

export function computeNavGridRoute(grid, start, end, opts = {}) {
  const maxSnap = Number(opts.maxSnapMeters) || 200;
  const graph =
    opts.graph || buildNavGridGraph(grid, { mergeMeters: opts.mergeMeters });
  const { nodes, nodesById, edges, edgeAscent, edgePaths } = graph;
  if (!nodes.length) return { ok: false, reason: "empty_grid" };
  const preference = opts?.preference === "low_ascent" ? "low_ascent" : "shortest";

  const findNearestNodeFast = (point) => {
    const idx = graph?.spatialIndex;
    if (
      idx &&
      Number.isFinite(idx.cellDeg) &&
      idx.cellDeg > 0 &&
      idx.buckets instanceof Map &&
      point &&
      Number.isFinite(point.lat) &&
      Number.isFinite(point.lon)
    ) {
      const x = Math.floor(Number(point.lon) / idx.cellDeg);
      const y = Math.floor(Number(point.lat) / idx.cellDeg);
      const metersPerCell = idx.cellDeg * 111320;
      const cellRadius = Math.max(
        1,
        Math.min(
          64,
          Math.ceil(maxSnap / Math.max(1e-6, metersPerCell)) + 1
        )
      );
      let best = null;
      let bestDist = Infinity;
      for (let dx = -cellRadius; dx <= cellRadius; dx += 1) {
        for (let dy = -cellRadius; dy <= cellRadius; dy += 1) {
          const ids = idx.buckets.get(`${x + dx}:${y + dy}`);
          if (!ids || !ids.length) continue;
          for (const id of ids) {
            const n = nodesById[id];
            if (!n) continue;
            const d = haversineMeters(n, point);
            if (d < bestDist) {
              best = n;
              bestDist = d;
            }
          }
        }
      }
      if (best && bestDist <= maxSnap) return { node: best, dist: bestDist };
      return null;
    }
    return findNearestNode(nodes, point, maxSnap);
  };

  const findNearestCandidatesFast = (point, limit = 8) => {
    const idx = graph?.spatialIndex;
    const maxItems = Math.max(1, Math.min(24, Number(limit) || 8));
    const out = [];
    if (
      idx &&
      Number.isFinite(idx.cellDeg) &&
      idx.cellDeg > 0 &&
      idx.buckets instanceof Map &&
      point &&
      Number.isFinite(point.lat) &&
      Number.isFinite(point.lon)
    ) {
      const x = Math.floor(Number(point.lon) / idx.cellDeg);
      const y = Math.floor(Number(point.lat) / idx.cellDeg);
      const metersPerCell = idx.cellDeg * 111320;
      const cellRadius = Math.max(
        1,
        Math.min(64, Math.ceil(maxSnap / Math.max(1e-6, metersPerCell)) + 1)
      );
      for (let dx = -cellRadius; dx <= cellRadius; dx += 1) {
        for (let dy = -cellRadius; dy <= cellRadius; dy += 1) {
          const ids = idx.buckets.get(`${x + dx}:${y + dy}`);
          if (!ids || !ids.length) continue;
          for (const id of ids) {
            const n = nodesById[id];
            if (!n) continue;
            const d = haversineMeters(n, point);
            if (!Number.isFinite(d) || d > maxSnap) continue;
            out.push({ node: n, dist: d });
          }
        }
      }
    } else {
      for (const n of nodes) {
        const d = haversineMeters(n, point);
        if (!Number.isFinite(d) || d > maxSnap) continue;
        out.push({ node: n, dist: d });
      }
    }
    if (!out.length) return [];
    out.sort((a, b) => a.dist - b.dist);
    const dedup = [];
    const seen = new Set();
    for (const item of out) {
      const id = String(item?.node?.id || "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      dedup.push(item);
      if (dedup.length >= maxItems) break;
    }
    return dedup;
  };

  let startCandidates = findNearestCandidatesFast(start, 8);
  let endCandidates = findNearestCandidatesFast(end, 8);
  const restrictCandidatesNearExactNode = (candidates) => {
    const best = Number(candidates?.[0]?.dist);
    if (!Number.isFinite(best)) return candidates;
    // Quando o ponto já foi projetado sobre um nó da rede, esse nó tem de ser
    // determinístico. Considerar outros nós próximos criava atalhos entre
    // ramais paralelos e fazia a rota mudar depois de voltar ao planeador.
    if (best <= 2) {
      return candidates.filter(
        (candidate) =>
          Number.isFinite(Number(candidate?.dist)) &&
          Number(candidate.dist) <= best + 1.5
      );
    }
    return candidates;
  };
  startCandidates = restrictCandidatesNearExactNode(startCandidates);
  endCandidates = restrictCandidatesNearExactNode(endCandidates);
  if (preference === "shortest") {
    const startBest = Number(startCandidates?.[0]?.dist);
    const endBest = Number(endCandidates?.[0]?.dist);
    const maxExtraSnap = Math.max(20, Math.min(maxSnap * 0.35, 60));
    if (Number.isFinite(startBest)) {
      startCandidates = startCandidates.filter(
        (c) => Number.isFinite(Number(c?.dist)) && Number(c.dist) <= startBest + maxExtraSnap
      );
    }
    if (Number.isFinite(endBest)) {
      endCandidates = endCandidates.filter(
        (c) => Number.isFinite(Number(c?.dist)) && Number(c.dist) <= endBest + maxExtraSnap
      );
    }
  }
  const startNodeInfo = startCandidates[0] || findNearestNodeFast(start);
  const endNodeInfo = endCandidates[0] || findNearestNodeFast(end);

  if (!startNodeInfo || !endNodeInfo || !startCandidates.length || !endCandidates.length) {
    return { ok: false, reason: "no_nearby_node" };
  }
  const startIds = startCandidates
    .map((item) => String(item?.node?.id || ""))
    .filter(Boolean);
  const endIdSet = new Set(
    endCandidates.map((item) => String(item?.node?.id || "")).filter(Boolean)
  );
  const endSnapById = {};
  endCandidates.forEach((item) => {
    const id = String(item?.node?.id || "");
    if (!id) return;
    const d = Number(item?.dist);
    if (!Number.isFinite(d)) return;
    if (!Number.isFinite(endSnapById[id]) || d < endSnapById[id]) {
      endSnapById[id] = d;
    }
  });

  class MinHeap {
    constructor() {
      this.items = [];
    }
    push(nodeId, primary, secondary = 0) {
      const item = { nodeId, primary, secondary };
      this.items.push(item);
      let idx = this.items.length - 1;
      while (idx > 0) {
        const parent = Math.floor((idx - 1) / 2);
        const p = this.items[parent];
        const c = this.items[idx];
        if (p.primary < c.primary) break;
        if (p.primary === c.primary && p.secondary <= c.secondary) break;
        [this.items[parent], this.items[idx]] = [this.items[idx], this.items[parent]];
        idx = parent;
      }
    }
    pop() {
      if (!this.items.length) return null;
      const top = this.items[0];
      const last = this.items.pop();
      if (this.items.length && last) {
        this.items[0] = last;
        let idx = 0;
        while (true) {
          const left = idx * 2 + 1;
          const right = left + 1;
          let smallest = idx;
          if (
            left < this.items.length &&
            (this.items[left].primary < this.items[smallest].primary ||
              (this.items[left].primary === this.items[smallest].primary &&
                this.items[left].secondary < this.items[smallest].secondary))
          ) {
            smallest = left;
          }
          if (
            right < this.items.length &&
            (this.items[right].primary < this.items[smallest].primary ||
              (this.items[right].primary === this.items[smallest].primary &&
                this.items[right].secondary < this.items[smallest].secondary))
          ) {
            smallest = right;
          }
          if (smallest === idx) break;
          [this.items[idx], this.items[smallest]] = [
            this.items[smallest],
            this.items[idx],
          ];
          idx = smallest;
        }
      }
      return top;
    }
    get size() {
      return this.items.length;
    }
  }

  const dist = {};
  const bestAscent = {};
  const bestDistance = {};
  const prev = {};
  const visited = new Set();
  const heap = new MinHeap();
  startCandidates.forEach((item) => {
    const id = String(item?.node?.id || "");
    if (!id) return;
    const snapDist = Math.max(0, Number(item?.dist) || 0);
    if (preference === "low_ascent") {
      bestAscent[id] = 0;
      bestDistance[id] = snapDist;
      heap.push(id, 0, snapDist);
    } else {
      dist[id] = snapDist;
      heap.push(id, snapDist, 0);
    }
    prev[id] = null;
  });

  while (heap.size) {
    const current = heap.pop();
    if (!current) break;
    const currentId = current.nodeId;
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    if (endIdSet.has(currentId)) break;

    const neighbors = edges[currentId] || {};
    const baseCost = Number.isFinite(dist[currentId]) ? dist[currentId] : Infinity;
    const baseAscent = Number.isFinite(bestAscent[currentId])
      ? bestAscent[currentId]
      : Infinity;
    const baseDistance = Number.isFinite(bestDistance[currentId])
      ? bestDistance[currentId]
      : Infinity;
    if (preference === "shortest" && !Number.isFinite(baseCost)) continue;
    if (preference === "low_ascent" && !Number.isFinite(baseAscent)) continue;

    for (const [nid, weightRaw] of Object.entries(neighbors)) {
      if (visited.has(nid)) continue;
      const weight = Number(weightRaw);
      if (!Number.isFinite(weight) || weight < 0) continue;
      const climb = Number(edgeAscent?.[currentId]?.[nid] || 0);
      if (preference === "low_ascent") {
        const nextAscent = baseAscent + Math.max(0, climb);
        const nextDistance = baseDistance + weight;
        const curAscent = Number.isFinite(bestAscent[nid]) ? bestAscent[nid] : Infinity;
        const curDistance = Number.isFinite(bestDistance[nid])
          ? bestDistance[nid]
          : Infinity;
        const better =
          nextAscent < curAscent ||
          (nextAscent === curAscent && nextDistance < curDistance);
        if (better) {
          bestAscent[nid] = nextAscent;
          bestDistance[nid] = nextDistance;
          prev[nid] = currentId;
          heap.push(nid, nextAscent, nextDistance);
        }
      } else {
        const alt = baseCost + weight;
        const currentBest = Number.isFinite(dist[nid]) ? dist[nid] : Infinity;
        if (alt < currentBest) {
          dist[nid] = alt;
          prev[nid] = currentId;
          heap.push(nid, alt, 0);
        }
      }
    }
  }

  let endId = null;
  let bestFinalPrimary = Infinity;
  let bestFinalSecondary = Infinity;
  endCandidates.forEach((item) => {
    const id = String(item?.node?.id || "");
    if (!id) return;
    const endSnap = Math.max(0, Number(item?.dist) || 0);
    if (preference === "low_ascent") {
      const ascent = Number.isFinite(bestAscent[id]) ? bestAscent[id] : Infinity;
      const routeDist = Number.isFinite(bestDistance[id]) ? bestDistance[id] : Infinity;
      if (!Number.isFinite(ascent) || !Number.isFinite(routeDist)) return;
      const finalDist = routeDist + endSnap;
      if (
        ascent < bestFinalPrimary ||
        (ascent === bestFinalPrimary && finalDist < bestFinalSecondary)
      ) {
        bestFinalPrimary = ascent;
        bestFinalSecondary = finalDist;
        endId = id;
      }
    } else {
      const routeDist = Number.isFinite(dist[id]) ? dist[id] : Infinity;
      if (!Number.isFinite(routeDist)) return;
      const finalDist = routeDist + endSnap;
      if (finalDist < bestFinalPrimary) {
        bestFinalPrimary = finalDist;
        endId = id;
      }
    }
  });

  const routeExists = !!endId;
  if (!routeExists) {
    return { ok: false, reason: "no_route" };
  }

  const pathIds = [];
  let cur = endId;
  while (cur != null) {
    pathIds.unshift(cur);
    cur = prev[cur];
  }

  const path = [];
  let distanceMetersReal = 0;
  let ascentGainMetersReal = 0;
  for (let i = 1; i < pathIds.length; i += 1) {
    const from = pathIds[i - 1];
    const to = pathIds[i];
    const stepDist = Number(edges?.[from]?.[to] || 0);
    if (Number.isFinite(stepDist) && stepDist > 0) {
      distanceMetersReal += stepDist;
    }
    const stepAscent = Number(edgeAscent?.[from]?.[to] || 0);
    if (Number.isFinite(stepAscent) && stepAscent > 0) {
      ascentGainMetersReal += stepAscent;
    }
    let segment = edgePaths?.[from]?.[to];
    if (!Array.isArray(segment) || !segment.length) {
      const reverseSegment = edgePaths?.[to]?.[from];
      if (Array.isArray(reverseSegment) && reverseSegment.length) {
        segment = [...reverseSegment].reverse();
      }
    }
    if (!Array.isArray(segment) || !segment.length) {
      const a = nodesById[from];
      const b = nodesById[to];
      segment = [a, b]
        .filter(Boolean)
        .map((p) => ({ lat: Number(p.lat), lon: Number(p.lon) }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    }
    if (!segment.length) continue;
    if (!path.length) {
      path.push(...segment);
      continue;
    }
    const last = path[path.length - 1];
    const first = segment[0];
    const samePoint =
      last &&
      first &&
      Number(last.lat) === Number(first.lat) &&
      Number(last.lon) === Number(first.lon);
    if (samePoint) {
      path.push(...segment.slice(1));
    } else {
      path.push(...segment);
    }
  }

  if (!path.length && pathIds.length) {
    const fallback = pathIds
      .map((id) => nodesById[id])
      .filter(Boolean)
      .map((p) => ({ lat: Number(p.lat), lon: Number(p.lon) }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    path.push(...fallback);
  }

  return {
    ok: true,
    path,
    distanceMeters:
      distanceMetersReal > 0
        ? distanceMetersReal
        : preference === "low_ascent"
        ? bestFinalSecondary
        : bestFinalPrimary,
    ascentGainMeters: Math.max(0, ascentGainMetersReal),
    startNodeId: pathIds[0] || startNodeInfo.node.id,
    endNodeId: endId,
  };
}

export async function ensureGraphElevationForLowAscent(
  graph,
  startNodeId,
  endNodeId,
  opts = {}
) {
  if (!graph || typeof graph !== "object") return false;
  if (!hasNavGridTerrainToken()) return false;

  const edges = graph.edges || {};
  const nodesById = graph.nodesById || {};
  if (!edges || !nodesById) return false;

  const startId = String(startNodeId || "");
  const endId = String(endNodeId || "");
  if (!startId || !endId || !nodesById[startId] || !nodesById[endId]) return false;

  const queue = [startId];
  const component = new Set([startId]);
  while (queue.length) {
    const id = queue.shift();
    if (id === endId && component.size > 32) {
      // already reached end and captured local component
      continue;
    }
    Object.keys(edges[id] || {}).forEach((nid) => {
      if (component.has(nid)) return;
      component.add(nid);
      queue.push(nid);
    });
  }

  if (!component.has(endId)) return false;

  const componentIds = Array.from(component);
  if (!componentIds.length) return false;

  const maxNodes = Math.max(50, Number(opts?.maxNodes) || 2500);
  const targetIds =
    componentIds.length > maxNodes ? componentIds.slice(0, maxNodes) : componentIds;

  let changed = false;
  const samples = [];
  targetIds.forEach((id) => {
    const node = nodesById[id];
    if (!node) return;
    if (Number.isFinite(Number(node.ele))) return;
    if (!Number.isFinite(Number(node.lat)) || !Number.isFinite(Number(node.lon))) return;
    samples.push({ id, lat: Number(node.lat), lon: Number(node.lon) });
  });

  // Keep concurrency bounded to avoid blocking UI/network.
  const concurrency = 24;
  let idx = 0;
  const workers = new Array(Math.min(concurrency, samples.length))
    .fill(null)
    .map(async () => {
      while (idx < samples.length) {
        const current = samples[idx];
        idx += 1;
        try {
          const ele = await sampleTerrainElevation(current.lat, current.lon, 13);
          if (!Number.isFinite(ele)) continue;
          const node = nodesById[current.id];
          if (!node) continue;
          if (!Number.isFinite(Number(node.ele))) {
            node.ele = ele;
            changed = true;
          }
        } catch {}
      }
    });
  if (workers.length) await Promise.all(workers);

  if (!graph.edgeAscent || typeof graph.edgeAscent !== "object") {
    graph.edgeAscent = {};
  }
  targetIds.forEach((fromId) => {
    if (!graph.edgeAscent[fromId] || typeof graph.edgeAscent[fromId] !== "object") {
      graph.edgeAscent[fromId] = {};
    }
    const fromEle = Number(nodesById?.[fromId]?.ele);
    if (!Number.isFinite(fromEle)) return;
    Object.keys(edges[fromId] || {}).forEach((toId) => {
      const existing = Number(graph.edgeAscent?.[fromId]?.[toId] || 0);
      if (existing > 0) return;
      const toEle = Number(nodesById?.[toId]?.ele);
      if (!Number.isFinite(toEle)) return;
      const climb = toEle - fromEle;
      if (climb > 0 && climb < 1200) {
        graph.edgeAscent[fromId][toId] = climb;
        changed = true;
      }
    });
  });

  if (changed) {
    graph.__hasTerrainElevation = true;
  }
  return changed;
}
